import { useState, useCallback, useRef, useEffect, useMemo, type ComponentType } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { cn } from "src/lib/cn";
import { sendAppEvent } from "src/Utils";
import StyledSelect from "../../StyledSelect";
import { useWorldbuildingTemplates, buildDerivedWorldbuildingRoles, buildWorldbuildingRoleOptions } from "../../Worldbuilding/useWorldbuildingTemplates";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { usePersistentChat } from "../PersistentChatContext";

type WorldbuildingRoleOption = ReturnType<typeof buildWorldbuildingRoleOptions>[number];
type StyledSelectProps = {
  value: WorldbuildingRoleOption | null;
  options: WorldbuildingRoleOption[];
  onChange: (option: WorldbuildingRoleOption | null) => void;
  placeholder?: string;
  "aria-label"?: string;
  menuPlacement?: "auto" | "bottom" | "top";
  menuPosition?: "absolute" | "fixed";
  menuPortalTarget?: HTMLElement | undefined;
  maxMenuHeight?: number;
  menuShouldScrollIntoView?: boolean;
  closeMenuOnScroll?: boolean;
  styleObjectOverrides?: Record<string, any>;
};

const TypedStyledSelect = StyledSelect as unknown as ComponentType<StyledSelectProps>;

type InputProps = {
  onGo: (
    customInstructions: string,
    extra?: {
      role?: string;
      roleLabel?: string;
      fields?: Array<{ id?: string; label: string; value?: unknown }>;
      roleExplicitlySelected?: boolean;
    },
  ) => void;
  onCancel: () => void;
};

export const GenerateWorldbuildingInput: React.FC<InputProps> = ({ onGo, onCancel }) => {
  const { projectId } = usePersistentChat();
  const { templates } = useWorldbuildingTemplates();
  const worldbuildingEntries = useStoryBibleStore((state) => state.worldbuildingEntries);
  const refreshWorldbuildingEntries = useStoryBibleStore((state) => state.refreshWorldbuildingEntries);
  const roleOptions = useMemo(
    () => buildWorldbuildingRoleOptions(templates, buildDerivedWorldbuildingRoles(worldbuildingEntries, templates)),
    [templates, worldbuildingEntries]
  );
  const [instructions, setInstructions] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>(roleOptions[0]?.value || "setting");
  const [roleExplicitlySelected, setRoleExplicitlySelected] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const worldbuildingFetchedForProjectRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedRole && roleOptions.length > 0) {
      setSelectedRole(roleOptions[0].value);
    }
  }, [roleOptions, selectedRole]);

  useEffect(() => {
    if (!projectId || worldbuildingFetchedForProjectRef.current === projectId) return;
    worldbuildingFetchedForProjectRef.current = projectId;
    void refreshWorldbuildingEntries(projectId, "GenerateWorldbuildingInput.mount");
  }, [projectId, refreshWorldbuildingEntries]);

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 300);
  }, []);

  const selectedOption = roleOptions.find((o) => o.value === selectedRole) || roleOptions[0] || null;

  const handleSubmit = useCallback(() => {
    const role =
      selectedRole || selectedOption?.value || roleOptions[0]?.value || "setting";
    const templateFields = roleExplicitlySelected ? selectedOption?.template?.defaultFields : undefined;
    onGo(instructions.trim(), {
      ...(roleExplicitlySelected
        ? { role, roleLabel: selectedOption?.label }
        : {}),
      ...(templateFields?.length ? { fields: templateFields } : {}),
      roleExplicitlySelected,
    });
  }, [instructions, selectedRole, selectedOption, roleOptions, roleExplicitlySelected, onGo]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <>
      {roleOptions.length > 0 && (
        <div className="w-full max-w-[200px]">
          <TypedStyledSelect
            value={selectedOption}
            options={roleOptions}
            onChange={(option) => {
              setSelectedRole(option?.value || roleOptions[0]?.value || "setting");
              setRoleExplicitlySelected(true);
              if (option?.value != null) {
                void sendAppEvent("Chat CTA - Worldbuilding Type Selected", {
                  type: option.value,
                  source: option.source ?? "unknown",
                });
              }
            }}
            placeholder="Select type"
            aria-label="Select the worldbuilding element type"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
            menuPlacement="auto"
            maxMenuHeight={200}
            menuShouldScrollIntoView={false}
            closeMenuOnScroll={false}
            styleObjectOverrides={{
              menuPortal: (base: any) => ({
                ...base,
                pointerEvents: "auto",
                zIndex: 30000,
              }),
              menuList: (base: any) => ({
                ...base,
                overscrollBehavior: "contain",
                paddingTop: 0,
                paddingBottom: 0,
              }),
            }}
          />
        </div>
      )}
      <TextareaAutosize
        ref={textareaRef}
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Tell Sudowrite how to write this (optional)..."
        className={cn(
          "w-full resize-none rounded-lg border border-solid border-app-grey-2 bg-app-document-background",
          "px-3 py-2 text-sm text-app-grey-0 placeholder:text-app-grey-1",
          "focus:outline-none focus:border-app-primary-cta",
          "font-[inherit]"
        )}
        minRows={2}
        maxRows={10}
      />
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onCancel}
          className={cn(
            "bg-transparent border-0 cursor-pointer",
            "text-app-grey-1 hover:text-app-grey-0 transition-colors",
            "text-sm font-medium px-2 py-1"
          )}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-1.5",
            "bg-app-primary-cta text-white border-0 cursor-pointer",
            "hover:brightness-110 active:scale-[0.98] transition-all",
            "text-sm font-semibold"
          )}
        >
          Go!
          <span className="flex items-center gap-0.5 opacity-70 text-xs">
            <kbd className="font-sans">⌘</kbd>
            <kbd className="font-sans">↵</kbd>
          </span>
        </button>
      </div>
    </>
  );
};
